import { Link, useNavigate } from "react-router-dom";
import { Star, MapPin, BadgeCheck, Heart, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";

const CAT_LABELS = {
  catering: "Catering",
  restaurant: "Restaurant",
  venue: "Venue",
  dj: "DJ",
  florist: "Florist",
  rentals: "Rentals",
  av: "A/V",
  security: "Security",
  photography: "Photography",
  transportation: "Transportation",
};

export default function VendorCard({ v, featured, eventId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const fav = useFavorites();
  const isFav = fav?.isFavorite(v.vendor_id) ?? false;
  const isFeatured = featured ?? v.featured;
  const label = CAT_LABELS[v.category] || v.category;
  const reviewsCount = v.reviews_count ?? v.reviews?.length ?? 0;
  // Carry the planner's selected event through to the profile so the booking
  // flow keeps its event context across navigation and refresh.
  const profileHref = eventId ? `/vendor/${v.vendor_id}?event=${encodeURIComponent(eventId)}` : `/vendor/${v.vendor_id}`;

  const onHeartClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save vendors", { description: "Create a free account to build a shortlist." });
      nav("/login");
      return;
    }
    try {
      const r = await fav.toggle(v.vendor_id);
      if (r?.saved) toast.success(`Saved ${v.name}`);
      else toast(`Removed ${v.name} from favorites`);
    } catch {
      toast.error("Couldn't update favorites");
    }
  };

  return (
    <div data-testid={`vendor-card-${v.vendor_id}`} className="card-elegant overflow-hidden group">
      <Link to={profileHref} className="block relative">
        <div className="aspect-[4/3] overflow-hidden bg-[color:var(--surface)]">
          {v.cover_image ? (
            <img
              src={v.cover_image}
              alt={v.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : null}
        </div>
        {isFeatured && (
          <span className="badge-featured absolute top-3 left-3">Featured</span>
        )}
        <button
          type="button"
          onClick={onHeartClick}
          data-testid={`vendor-fav-${v.vendor_id}`}
          aria-label={isFav ? "Remove from favorites" : "Save vendor"}
          aria-pressed={isFav}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 backdrop-blur flex items-center justify-center hover:bg-white transition-colors shadow-sm"
        >
          <Heart className={`w-4 h-4 ${isFav ? "fill-[#D9583A] text-[#D9583A]" : "text-[#1F1F1F]"}`} />
        </button>
      </Link>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base sm:text-lg font-semibold leading-snug truncate">{v.name}</h3>
              {v.verified && (
                <span className="badge-verified" data-testid={`verified-badge-${v.vendor_id}`}>
                  <BadgeCheck className="w-3 h-3" /> Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm text-[color:var(--text-muted)] mt-1">
              <MapPin className="w-3.5 h-3.5" /> <span className="truncate">{v.city} · {label}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Star className="w-4 h-4 fill-[#F5A524] text-[#F5A524]" />
            <span className="text-sm font-semibold">{(v.rating || 0).toFixed(1)}</span>
            {reviewsCount > 0 && (
              <span className="text-xs text-[color:var(--text-muted)]">({reviewsCount})</span>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-baseline gap-1">
          <span className="text-xs text-[color:var(--text-muted)]">from</span>
          <span className="text-xl font-semibold">${v.price_from?.toLocaleString?.() ?? v.price_from}</span>
          <span className="text-xs text-[color:var(--text-muted)]">/ {v.price_unit || "event"}</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <Link
            to={profileHref}
            data-testid={`vendor-view-${v.vendor_id}`}
            className="h-11 flex items-center justify-center gap-2 rounded-lg border border-[color:var(--border-strong)] bg-white text-sm font-semibold hover:bg-[color:var(--surface)] transition-colors"
          >
            View profile
          </Link>
          <Link
            to={profileHref}
            data-testid={`vendor-request-${v.vendor_id}`}
            className="h-11 flex items-center justify-center gap-2 rounded-lg bg-[color:var(--accent)] text-white text-sm font-semibold hover:bg-[color:var(--accent-hover)] transition-colors"
          >
            <MessageSquare className="w-4 h-4" /> Request quote
          </Link>
        </div>
      </div>
    </div>
  );
}
